import { t } from "i18next";

import type { ColumnsClarFactory } from "@/core/components/shared/data-table";
import { Badge } from "@/core/components/ui/badge";
import { NoTranslate } from "@/core/components/ui/no-translate";
import type { ProviderDto } from "@/features/ledger/api/dto";
import { ProviderRowActions } from "@/features/ledger/components/providers/provider-row-actions";
import {
  PROVIDER_STATUSES,
  PROVIDER_TYPES,
  type ProviderStatus,
} from "@/features/shared/api/enums";

/** Ligne de la liste : `DataTableClar` exige un `id` requis (optionnel dans le DTO). */
export type ProviderRow = ProviderDto & { id: string };

const STATUS_VARIANTS: Record<ProviderStatus, "success" | "secondary"> = {
  ACTIVE: "success",
  INACTIVE: "secondary",
};

/**
 * Colonnes des providers (`POST /Providers/all`). Filtres : `code`,
 * `name` (texte), `type` et `status` (select).
 */
export const providersColumns: ColumnsClarFactory<ProviderRow> = (navigate) => [
  {
    id: "code",
    accessorKey: "code",
    header: t("ledger.providers.columns.code"),
    cell: ({ getValue }) => (
      <NoTranslate as="span" className="font-mono text-xs">
        {(getValue() as string) || "—"}
      </NoTranslate>
    ),
    meta: { type: "text" },
  },
  {
    id: "name",
    accessorKey: "name",
    header: t("ledger.providers.columns.name"),
    cell: ({ getValue }) => (
      <NoTranslate as="span">{(getValue() as string) || "—"}</NoTranslate>
    ),
    meta: { type: "text" },
  },
  {
    id: "type",
    accessorKey: "type",
    header: t("ledger.providers.columns.type"),
    cell: ({ getValue }) => (
      <NoTranslate as="span">
        <Badge variant="secondary">{getValue() as string}</Badge>
      </NoTranslate>
    ),
    meta: {
      type: "select",
      options: PROVIDER_TYPES.map((value) => ({ value, label: value })),
    },
  },
  {
    id: "status",
    accessorKey: "status",
    header: t("ledger.providers.columns.status"),
    cell: ({ getValue }) => {
      const status = getValue() as ProviderStatus | undefined;
      return status ? (
        <NoTranslate as="span">
          <Badge variant={STATUS_VARIANTS[status] ?? "outline"}>{status}</Badge>
        </NoTranslate>
      ) : (
        "—"
      );
    },
    meta: {
      type: "select",
      options: PROVIDER_STATUSES.map((value) => ({ value, label: value })),
    },
  },
  {
    id: "actions",
    header: t("common.actions"),
    enableHiding: false,
    cell: ({ row }) =>
      row.original.id ? (
        <ProviderRowActions provider={row.original} navigate={navigate} />
      ) : null,
    meta: { type: "denied" },
  },
];
